
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent 
} from "@/components/ui/card";
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { ResizableSwappableCard } from "@/components/admin/dashboard/ResizableSwappableCard";
import { ExpirySticker } from "@/components/contracts/table/ExpirySticker";
import { Contract } from "@/lib/types";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";

interface RecentContractsTableProps {
  recentContracts: any[];
  rearrangingEnabled: boolean;
  onRemove: (id: string) => void;
  size: 'small' | 'medium' | 'large' | 'xlarge';
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
}

export function RecentContractsTable({
  recentContracts,
  rearrangingEnabled,
  onRemove,
  size,
  onResize
}: RecentContractsTableProps) {
  const { t } = useEnhancedTranslation();

  return (
    <ResizableSwappableCard
      id="recent-contracts"
      size={size}
      showHandle={rearrangingEnabled}
      showResizeControl={rearrangingEnabled}
      onRemove={rearrangingEnabled ? onRemove : undefined}
      onResize={rearrangingEnabled && onResize ? (newSize) => onResize("recent-contracts", newSize) : undefined}
    >
      <CardHeader>
        <CardTitle>{t('dashboard.widgets.recentContracts', 'Recent Contracts')}</CardTitle>
      </CardHeader>
      <CardContent>
        {recentContracts.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('dashboard.widgets.supplier', 'Supplier')}</TableHead>
                <TableHead>{t('dashboard.widgets.status', 'Status')}</TableHead>
                <TableHead>{t('dashboard.widgets.created', 'Created')}</TableHead>
                <TableHead className="text-right">{t('dashboard.widgets.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentContracts.map((contract) => (
                <TableRow key={contract.id}>
                  <TableCell className="font-medium">{contract.supplier_name}</TableCell>
                  <TableCell>
                    {contract.expiry_date && (
                      <ExpirySticker 
                        contract={{
                          id: contract.id,
                          supplierName: contract.supplier_name,
                          importance: contract.importance || 1,
                          expiryDate: new Date(contract.expiry_date),
                          typeId: contract.contract_type_id || "",
                          expectedCost: contract.expected_cost || 0,
                          currencyId: contract.currency_id || "",
                          signedDate: new Date(contract.signed_date || contract.created_at),
                          userId: contract.user_id || "",
                          type: {
                            id: contract.contract_type_id || "",
                            name: contract.contract_types?.name || contract.type || "",
                            auto_renews: contract.contract_types?.auto_renews || false
                          },
                          // Add missing required properties
                          paymentFrequency: { interval: 1, unit: "Month" },
                          reminderPeriod: "1 month",
                          reminderFrequency: "Once",
                          categoryId: "",
                          contactPersons: [],
                          attachments: [],
                          created_at: contract.created_at || new Date().toISOString(),
                          updated_at: contract.updated_at || new Date().toISOString()
                        }} 
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDistanceToNow(new Date(contract.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" asChild>
                      <Link to={`/contracts/${contract.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-muted-foreground">
            {t('dashboard.widgets.noRecentContracts', 'No recent contracts found')}
          </div>
        )}
      </CardContent>
    </ResizableSwappableCard>
  );
}
